import type {
  SignUpRequest,
  SignUpWithMerchantRequest,
  SignInRequest,
  AuthResponse,
  UserResponse,
  ChangePasswordRequest,
  DeleteAccountRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  SwitchMerchantRequest,
  SwitchProfileRequest,
  ImpersonateEmployeeRequest,
  InviteUsersRequest,
  InviteUsersResponse,
  AddUserRequest,
  AddUserResponse,
  UpdateUserRoleRequest,
  DeleteUserRoleRequest,
  TotpResponse,
  RecoveryCodesResponse,
  PhoneOtpRequest,
  PhoneOtpResponse,
  PhoneOtpVerifyRequest,
  PhoneOtpVerifyResponse,
  UpdateMetadataRequest,
  SetUserPreferenceRequest,
  UserPreferenceResponse,
  UpdateUserDetailsRequest,
  FromEmailRequest,
  TokenResponse,
  UserInvitation,
  AcceptInvitationsPreAuthRequest,
  VerifyTotpRequest,
  Terminate2faQueryParams,
  ListInvitableRolesParams,
  ListUsersInLineageParams,
  UserInLineage,
  LoginHistoryParams,
  LoginHistoryResponse,
  UserSessionListResponse,
  UserSessionRevokeResponse,
  ParentGroupInfo,
  RoleConnectorGrant,
  UpdateRoleConnectorGrantParams,
} from '../types';
import type { RequestFn } from '../client';

export class Users {
  constructor(private readonly request: RequestFn) {}

  async signUp(params: SignUpRequest | SignUpWithMerchantRequest): Promise<AuthResponse> {
    return this.request('POST', '/user/signup', { body: params });
  }

  async signIn(params: SignInRequest): Promise<AuthResponse> {
    return this.request('POST', '/user/signin', { body: params });
  }

  async signOut(): Promise<Record<string, unknown>> {
    return this.request('POST', '/user/signout');
  }

  /**
   * Sliding-session refresh: exchange the current (still-valid) login JWT
   * for a fresh one with the same claims and a full lifetime. The backend
   * keeps the session's identity (`jti`), slides `user_session.expires_at`
   * forward and re-sets the `login_token` cookie.
   *
   * Requires a token backed by a revocable session (a `jti` claim). Signin
   * and switch-merchant/-profile tokens have one; **session-less tokens do
   * not and are rejected with 400** — team-impersonation tokens are the
   * case in practice, and they are deliberately tab-scoped and
   * time-bounded rather than renewable. A 400 here is not a dead session:
   * the token remains valid for ordinary calls, it simply cannot slide.
   *
   * Rejected (401) for expired, blacklisted or revoked tokens — refresh can
   * only extend a session that is still alive. Rate-limited server-side
   * (429) to one mint per session per minute; treat a 429 as "still fresh
   * enough", not as an error.
   *
   * The returned token is NOT applied to this client automatically — pass
   * it to `setJwtToken()`, or use {@link Delopay.refreshSession} which does
   * both.
   *
   * `POST /user/token/refresh`. Requires a logged-in JWT.
   */
  async refreshToken(): Promise<TokenResponse> {
    return this.request('POST', '/user/token/refresh');
  }

  /**
   * Paginated login history for the authenticated user -- IP, User-Agent,
   * country / city / lat-lon (when GeoIP is enabled), success and failure
   * events with their reasons. Strictly scoped to the JWT subject; a user
   * can only see their own activity.
   *
   * `GET /user/me/login-activity`. Requires a logged-in JWT.
   *
   * Returns an empty page when a Delopay admin is impersonating a merchant,
   * so the admin's metadata is not exposed inside the merchant dashboard.
   */
  async listLoginActivity(params?: LoginHistoryParams): Promise<LoginHistoryResponse> {
    if (params === undefined) {
      return this.request('GET', '/user/me/login-activity');
    }
    return this.request('GET', '/user/me/login-activity', {
      query: params as Record<string, number | undefined>,
    });
  }

  /**
   * List the authenticated user's currently-active dashboard sessions
   * (one row per minted login JWT that hasn't been revoked or expired).
   *
   * The row matching the JWT making this call has `is_current: true`,
   * which is what lets the dashboard render a "This device" tag.
   *
   * `GET /user/me/sessions`. Requires a logged-in JWT. Returns an empty
   * list when a Delopay admin is impersonating a non-admin merchant
   * (same guard as `listLoginActivity`).
   */
  async listActiveSessions(): Promise<UserSessionListResponse> {
    return this.request('GET', '/user/me/sessions');
  }

  /**
   * Disconnect one of the authenticated user's sessions. The matching
   * JWT is rejected on its next request — fast-path via Redis, fall back
   * to the persistent `revoked_at` column.
   *
   * Idempotent: revoking an already-revoked or unknown id returns 404,
   * which the caller can treat as success for retry purposes. Revoking
   * a session id that belongs to a different user also returns 404 —
   * the response intentionally doesn't leak whether the id exists.
   *
   * `POST /user/me/sessions/{sessionId}/revoke`.
   */
  async revokeSession(sessionId: string): Promise<UserSessionRevokeResponse> {
    return this.request('POST', `/user/me/sessions/${encodeURIComponent(sessionId)}/revoke`);
  }

  async getDetails(): Promise<UserResponse> {
    return this.request('GET', '/user');
  }

  async update(params: UpdateUserDetailsRequest): Promise<UserResponse> {
    return this.request('POST', '/user/update', { body: params });
  }

  /**
   * Read a preference owned by the logged-in user. Missing keys return value null.
   * Works in both dashboards with a login JWT; API keys and impersonation are rejected.
   * Keys contain 1–128 ASCII letters, digits, dots, underscores or hyphens.
   * Unknown widget IDs and their order are preserved; consumers choose what to render.
   */
  async getPreference(key: string): Promise<UserPreferenceResponse> {
    return this.request('GET', `/user/preferences/${encodeURIComponent(key)}`);
  }

  /**
   * Save one own preference: omitted/false if_absent replaces it (last write wins);
   * true preserves an existing object and returns the winner. The JSON object is
   * limited to 16 KiB of compact UTF-8 JSON. For layouts, use one namespaced key
   * per page (e.g. merchant.analytics.payments) and ordered widget IDs per region,
   * without titles, fetched metrics or rendered state. Requires a login JWT.
   */
  async setPreference(
    key: string,
    params: SetUserPreferenceRequest,
  ): Promise<UserPreferenceResponse> {
    return this.request('PUT', `/user/preferences/${encodeURIComponent(key)}`, { body: params });
  }

  /**
   * Initialize only a missing preference. Existing objects, including a client
   * reset marker saved with setPreference (for example { reset: true }), win. Render/cache the returned value, which may differ from
   * the proposed value. Safe for migration retries after a lost response.
   * Explicit edits and reset markers use setPreference with no if_absent flag.
   * DELETE removes the key; it leaves no marker and permits initialization again.
   * Requires a login JWT and the same key/object limits as setPreference.
   */
  async initializePreference(
    key: string,
    value: Record<string, unknown>,
  ): Promise<UserPreferenceResponse> {
    return this.request('PUT', `/user/preferences/${encodeURIComponent(key)}`, {
      body: { value, if_absent: true },
    });
  }

  /**
   * Remove an own preference. Idempotent; returns value null, without storing a
   * tombstone. Later initialization may import a value again. To suppress legacy
   * migration after a reset, save a client reset-marker object with setPreference.
   * Login JWT only.
   */
  async deletePreference(key: string): Promise<UserPreferenceResponse> {
    return this.request('DELETE', `/user/preferences/${encodeURIComponent(key)}`);
  }

  /**
   * RFC 7396 merge-patch the caller's own user-scoped metadata bucket.
   * Returns the full user details, so callers can refresh their context
   * without a second fetch.
   *
   * `PATCH /user/metadata`
   */
  async updateMetadata(params: UpdateMetadataRequest): Promise<UserResponse> {
    return this.request('PATCH', '/user/metadata', { body: params });
  }

  /**
   * RFC 7396 merge-patch the merchant-scoped metadata bucket shared by
   * every dashboard user of the merchant. Same response contract as
   * {@link Users.updateMetadata}.
   *
   * `PATCH /user/merchant/metadata`
   */
  async updateMerchantMetadata(params: UpdateMetadataRequest): Promise<UserResponse> {
    return this.request('PATCH', '/user/merchant/metadata', { body: params });
  }

  /**
   * Permanently delete the caller's account. Requires a fresh password
   * (and a current 6-digit TOTP code if the user has TOTP enrolled). On
   * success all role assignments are removed, the user record is
   * deactivated, and all in-flight sessions are invalidated. The caller
   * should clear local credentials and route to the login page.
   *
   * Returns `InvalidDeleteOperation` when the caller is the sole
   * owner-level admin of an org / merchant / profile -- they must
   * transfer ownership first.
   */
  async deleteAccount(params: DeleteAccountRequest): Promise<Record<string, unknown>> {
    return this.request('DELETE', '/user/account', { body: params });
  }

  async changePassword(params: ChangePasswordRequest): Promise<UserResponse> {
    return this.request('POST', '/user/change-password', { body: params });
  }

  async rotatePassword(params: ResetPasswordRequest): Promise<UserResponse> {
    return this.request('POST', '/user/rotate-password', { body: params });
  }

  async forgotPassword(params: ForgotPasswordRequest): Promise<Record<string, unknown>> {
    return this.request('POST', '/user/forgot-password', { body: params });
  }

  /**
   * Commit a password reset.
   *
   * The caller is responsible for obtaining a `SinglePurposeToken` with
   * `purpose: reset_password` via the email-token exchange + TOTP flow
   * (see `fromEmail`, `beginTotp`, `updateTotp`/`verifyTotp`,
   * `generateRecoveryCodes`, `terminate2fa`) and setting it on the client
   * via `setJwtToken` before calling this method. `body.token` must still
   * be the original `EmailToken` from the reset-link URL — the handler
   * decodes it a second time to find the user.
   */
  async resetPassword(params: ResetPasswordRequest): Promise<Record<string, unknown>> {
    return this.request('POST', '/user/reset-password', { body: params });
  }

  /**
   * Exchange an email-link token (`EmailToken`) for a single-purpose JWT
   * that drives the next step of the flow (TOTP, verify email, accept
   * invitation, etc.). No authentication required.
   *
   * The `token_type` in the response tells you which step to run next.
   */
  async fromEmail(params: FromEmailRequest): Promise<TokenResponse> {
    return this.request('POST', '/user/from-email', { body: params });
  }

  async verifyEmail(params: Record<string, unknown>): Promise<AuthResponse> {
    return this.request('POST', '/user/verify-email', { body: params });
  }

  async sendVerificationEmail(params: ForgotPasswordRequest): Promise<Record<string, unknown>> {
    return this.request('POST', '/user/verify-email-request', { body: params });
  }

  async createMerchant(params: Record<string, unknown>): Promise<AuthResponse> {
    return this.request('POST', '/user/create-merchant', { body: params });
  }

  async switchMerchant(params: SwitchMerchantRequest): Promise<AuthResponse> {
    return this.request('POST', '/user/switch/merchant', { body: params });
  }

  async switchProfile(params: SwitchProfileRequest): Promise<AuthResponse> {
    return this.request('POST', '/user/switch/profile', { body: params });
  }

  async listMerchants(): Promise<Record<string, unknown>[]> {
    return this.request('GET', '/user/list/merchant');
  }

  async listProfiles(): Promise<Record<string, unknown>[]> {
    return this.request('GET', '/user/list/profile');
  }

  async inviteUsers(params: InviteUsersRequest[]): Promise<InviteUsersResponse[]> {
    return this.request('POST', '/user/employees/invite', { body: params });
  }

  /**
   * Add a team member directly, without sending an invite email.
   * `POST /user/employees/add`
   *
   * Unlike `inviteUsers`, the account is active immediately and you hand over
   * the credentials yourself. Omit `password` to have the server generate one
   * and return it once in `password` on the response; supply your own and it is
   * not echoed back. Either way the member must change it on first sign-in.
   *
   * Same role rules as invite: you cannot grant a role above your own, and a
   * shop-scoped caller can only target their own shop.
   */
  async addUser(params: AddUserRequest): Promise<AddUserResponse> {
    return this.request('POST', '/user/employees/add', { body: params });
  }

  /**
   * Impersonate one of your own team members — `POST /user/employees/impersonate`.
   *
   * Mints a session token **as** the given member, so the dashboard renders
   * exactly what they see (useful for support and role verification). The
   * caller needs the *Impersonation* permission, and the member's role must
   * rank **strictly below** the caller's (`Profile < Merchant < Organization`);
   * the server rejects self-impersonation, cross-merchant targets, and
   * equal/higher roles.
   *
   * The returned token is tab-scoped by design: open it in a fresh tab (e.g.
   * `/auth/impersonate?token=…`) rather than replacing the caller's own
   * session. No auth cookie is set on the response.
   */
  async impersonateEmployee(params: ImpersonateEmployeeRequest): Promise<TokenResponse> {
    return this.request('POST', '/user/employees/impersonate', { body: params });
  }

  async acceptInvitation(params: Record<string, unknown>): Promise<AuthResponse> {
    return this.request('POST', '/user/employees/invite/accept', { body: params });
  }

  /**
   * Accept invitations before a session exists.
   *
   * Sign-in issues a `token_type: "accept_invite"` step token instead of a
   * session when the caller has no active role yet — every role is still an
   * unaccepted invitation. With that token set via `setJwtToken`, pass the
   * entities from `listInvitations` here; the response is the next step of
   * the sign-in flow (`user_info` for the session, or `force_set_password`
   * first). Rejected when none of the entities carries a pending invitation.
   */
  async acceptInvitationsPreAuth(params: AcceptInvitationsPreAuthRequest): Promise<TokenResponse> {
    return this.request('POST', '/user/employees/invite/accept/pre-auth', { body: params });
  }

  /**
   * Accept an invitation via the email-link flow.
   *
   * Caller must already hold a `SinglePurposeToken` with
   * `purpose: accept_invitation_from_email` (obtained via `fromEmail` + any
   * required TOTP step) and have set it on the client via `setJwtToken`.
   * `body.token` must still be the original `EmailToken` from the
   * invite-link URL — the handler decodes it a second time to find the
   * invitee and the entity lineage.
   */
  async acceptInviteFromEmail(params: FromEmailRequest): Promise<AuthResponse> {
    return this.request('POST', '/user/accept-invite-from-email', { body: params });
  }

  /**
   * Start TOTP setup (or no-op if already set).
   *
   * Returns the QR-code payload when the user has no TOTP configured yet;
   * returns `{ secret: null }` when the user is already set up (caller
   * should then prompt for a 6-digit code and call `verifyTotp`).
   *
   * Requires `Authorization: Bearer <SPT{purpose:totp}>`.
   */
  async beginTotp(): Promise<TotpResponse> {
    return this.request('GET', '/user/2fa/totp/begin');
  }

  /**
   * Verify a 6-digit TOTP code for a user whose TOTP is already set up.
   * Marks the code as used in Redis so subsequent flow steps can advance.
   *
   * Requires `Authorization: Bearer <SPT{purpose:totp}>`.
   */
  async verifyTotp(params: VerifyTotpRequest): Promise<Record<string, unknown>> {
    return this.request('POST', '/user/2fa/totp/verify', { body: params });
  }

  async resetTotp(): Promise<Record<string, unknown>> {
    return this.request('GET', '/user/2fa/totp/reset');
  }

  async generateRecoveryCodes(): Promise<RecoveryCodesResponse> {
    return this.request('GET', '/user/2fa/recovery-code/generate');
  }

  async verifyRecoveryCode(params: Record<string, unknown>): Promise<AuthResponse> {
    return this.request('POST', '/user/2fa/recovery-code/verify', { body: params });
  }

  async sendPhoneOtp(params: PhoneOtpRequest): Promise<PhoneOtpResponse> {
    return this.request('POST', '/user/phone/send-otp', { body: params });
  }

  async verifyPhoneOtp(params: PhoneOtpVerifyRequest): Promise<PhoneOtpVerifyResponse> {
    return this.request('POST', '/user/phone/verify-otp', { body: params });
  }

  /**
   * List all roles visible to the caller (predefined + custom).
   *
   * `GET /user/role/list`. With `groups: true` the response is the
   * parent-groups shape: `[{role_id, role_name, entity_type, role_scope,
   * parent_groups: [{name, description, scopes}]}]`; without it, the
   * deprecated flat `groups` shape.
   */
  async listRoles(params?: {
    groups?: boolean;
    entity_type?: string;
  }): Promise<Record<string, unknown>[]> {
    if (params === undefined) {
      return this.request('GET', '/user/role/list');
    }
    return this.request('GET', '/user/role/list', {
      query: { groups: params.groups, entity_type: params.entity_type },
    });
  }

  async listUserRoles(params?: Record<string, unknown>): Promise<Record<string, unknown>[]> {
    return this.request('POST', '/user/employees', { body: params });
  }

  /**
   * Change a team member's role. `POST /user/employees/update-role`
   *
   * Pass `profile_id` to name the shop when managing a shop's team as a
   * merchant-scoped admin — see {@link UpdateUserRoleRequest.profile_id}.
   */
  async updateUserRole(params: UpdateUserRoleRequest): Promise<Record<string, unknown>> {
    return this.request('POST', '/user/employees/update-role', { body: params });
  }

  /**
   * Remove a team member. `DELETE /user/employees/delete`
   *
   * Pass `profile_id` to name the shop when managing a shop's team as a
   * merchant-scoped admin — see {@link DeleteUserRoleRequest.profile_id}.
   */
  async deleteUserRole(params: DeleteUserRoleRequest): Promise<Record<string, unknown>> {
    return this.request('DELETE', '/user/employees/delete', { body: params });
  }

  /** Sign in via OIDC. `POST /user/oidc` */
  async signInOidc(params: Record<string, unknown>): Promise<AuthResponse> {
    return this.request('POST', '/user/oidc', { body: params });
  }

  /** Transfer key. `POST /user/key/transfer` */
  async transferKey(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/user/key/transfer', { body: params });
  }

  /**
   * Invitations still waiting on the caller. `GET /user/list/invitation`
   *
   * Accepts an `accept_invite` step token as well as a session JWT, so it
   * can feed `acceptInvitationsPreAuth` during sign-in.
   */
  async listInvitations(): Promise<UserInvitation[]> {
    return this.request('GET', '/user/list/invitation');
  }

  /** Check 2FA status. `GET /user/2fa` */
  async check2faStatus(): Promise<Record<string, unknown>> {
    return this.request('GET', '/user/2fa');
  }

  /**
   * Finish first-time TOTP setup: commit the secret generated by `beginTotp`
   * against a 6-digit code from the user's authenticator app.
   *
   * `PUT /user/2fa/totp/verify`. Requires `Authorization: Bearer <SPT{purpose:totp}>`.
   */
  async updateTotp(params: VerifyTotpRequest): Promise<Record<string, unknown>> {
    return this.request('PUT', '/user/2fa/totp/verify', { body: params });
  }

  /**
   * Complete the TOTP step and advance to the next flow stage (e.g.
   * `reset_password`). Returns a fresh single-purpose token with the
   * next `token_type`.
   *
   * `GET /user/2fa/terminate`. Requires `Authorization: Bearer <SPT{purpose:totp}>`.
   */
  async terminate2fa(query?: Terminate2faQueryParams): Promise<TokenResponse> {
    if (query === undefined) {
      return this.request('GET', '/user/2fa/terminate');
    }
    return this.request('GET', '/user/2fa/terminate', {
      query: query as Record<string, boolean | undefined>,
    });
  }

  /** Create auth method. `POST /user/auth` */
  async createAuthMethod(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/user/auth', { body: params });
  }

  /** Update auth method. `PUT /user/auth` */
  async updateAuthMethod(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('PUT', '/user/auth', { body: params });
  }

  /** List auth methods. `GET /user/auth/list` */
  async listAuthMethods(): Promise<Record<string, unknown>[]> {
    return this.request('GET', '/user/auth/list');
  }

  /** Get auth URL. `GET /user/auth/url` */
  async getAuthUrl(): Promise<Record<string, unknown>> {
    return this.request('GET', '/user/auth/url');
  }

  /** Select auth method. `POST /user/auth/select` */
  async selectAuth(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/user/auth/select', { body: params });
  }

  /**
   * List users in lineage.
   *
   * Needs the Users *view* grant now — the response carries colleagues' email
   * addresses, so a role without it is refused rather than handed a roster.
   * A shop-scoped role keeps reading its own shop's members.
   *
   * `GET /user/employees/list`
   */
  async listUsersInLineage(params?: ListUsersInLineageParams): Promise<UserInLineage[]> {
    return this.request('GET', '/user/employees/list', {
      query: params as Record<string, string | undefined> | undefined,
    });
  }

  /** Resend invite. `POST /user/resend-invite` */
  async resendInvite(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/user/resend-invite', { body: params });
  }

  /**
   * Get the caller's parent permission groups + scopes.
   *
   * `GET /user/role`
   */
  async getRolePermissions(): Promise<ParentGroupInfo[]> {
    return this.request('GET', '/user/role');
  }

  /**
   * List invitable roles. `GET /user/role/list/invite`
   *
   * @param params - Optional query. `entity_type` scopes the role list to a
   *   particular entity (e.g. `'merchant'` to list only merchant-scoped roles
   *   when inviting employees from the merchant dashboard).
   */
  async listInvitableRoles(params?: ListInvitableRolesParams): Promise<Record<string, unknown>[]> {
    if (params === undefined || params.entity_type === undefined) {
      return this.request('GET', '/user/role/list/invite');
    }
    return this.request('GET', '/user/role/list/invite', {
      query: { entity_type: params.entity_type },
    });
  }

  /** List updatable roles. `GET /user/role/list/update` */
  async listUpdatableRoles(): Promise<Record<string, unknown>[]> {
    return this.request('GET', '/user/role/list/update');
  }

  /** Get parent list. `GET /user/parent/list` */
  async getParentList(): Promise<Record<string, unknown>[]> {
    return this.request('GET', '/user/parent/list');
  }

  /** Create a role. `POST /user/role` */
  async createRole(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/user/role', { body: params });
  }

  /** Get role by ID. `GET /user/role/{roleId}` */
  async getRoleById(roleId: string): Promise<Record<string, unknown>> {
    return this.request('GET', `/user/role/${encodeURIComponent(roleId)}`);
  }

  /** Update role by ID. `PUT /user/role/{roleId}` */
  async updateRole(
    roleId: string,
    params: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request('PUT', `/user/role/${encodeURIComponent(roleId)}`, { body: params });
  }

  /**
   * Delete a custom role. Predefined roles and roles still assigned to
   * team members are rejected by the backend with a 400.
   *
   * `DELETE /user/role/{roleId}`
   */
  async deleteRole(roleId: string): Promise<Record<string, unknown>> {
    return this.request('DELETE', `/user/role/${encodeURIComponent(roleId)}`);
  }

  /**
   * Read which individual connector accounts a role may see.
   *
   * `GET /user/role/{roleId}/connectors`
   *
   * **Check `restricted` before reading `connectors`.** An empty list is
   * ambiguous by itself, so the backend states which case it is: `false` means
   * the role holds no grant and sees whatever its entity and profile scope
   * already allowed. Rendering an empty `connectors` array as "this role sees
   * nothing" inverts the meaning.
   *
   * Requires the permission that *edits a role*, not a connector permission.
   */
  async getRoleConnectors(roleId: string): Promise<RoleConnectorGrant> {
    return this.request('GET', `/user/role/${encodeURIComponent(roleId)}/connectors`);
  }

  /**
   * Replace the set of connector accounts a role may see.
   *
   * `PUT /user/role/{roleId}/connectors`
   *
   * The call **replaces** the whole set rather than adding to it, so send the
   * complete list every time. An empty `merchant_connector_ids` clears the
   * grant and returns the role to unrestricted.
   *
   * The backend refuses an id that is not a connector account of the caller's
   * own merchant, an `Organization`-scoped role (a connector account belongs to
   * exactly one merchant, so an org-spanning role cannot hold one coherently),
   * and any predefined role (one static entry shared by every tenant).
   *
   * Editing a grant invalidates the role cache and blacklists tokens minted
   * before the edit, so **users holding this role must sign in again**. Worth
   * saying in the UI before the save, not after.
   */
  async updateRoleConnectors(
    roleId: string,
    params: UpdateRoleConnectorGrantParams,
  ): Promise<RoleConnectorGrant> {
    return this.request('PUT', `/user/role/${encodeURIComponent(roleId)}/connectors`, {
      body: params,
    });
  }
}
